import React, { useState } from 'react';

const UserProfile = ({ user, onBack, onUserUpdate }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [emailForm, setEmailForm] = useState({
    new_email: '',
    current_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [emailMessage, setEmailMessage] = useState({ type: '', text: '' });
  const [currentUser, setCurrentUser] = useState(user);

  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/user/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        setTimeout(() => {
          setShowPasswordForm(false);
          setMessage({ type: '', text: '' });
        }, 2000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async () => {
    if (!emailForm.new_email || !emailForm.current_password) {
      setEmailMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.new_email)) {
      setEmailMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    if (emailForm.new_email === currentUser.email) {
      setEmailMessage({ type: 'error', text: 'New email is the same as your current email' });
      return;
    }

    setEmailLoading(true);
    setEmailMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/user/update-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_email: emailForm.new_email,
          current_password: emailForm.current_password
        })
      });

      const data = await response.json();

      if (response.ok) {
        const updatedUser = { ...currentUser, email: emailForm.new_email };
        setEmailMessage({ type: 'success', text: 'Email address updated successfully' });
        setCurrentUser(updatedUser);
        
        // Update the parent component's user state if callback is provided
        if (onUserUpdate) {
          onUserUpdate(updatedUser);
        }
        
        setEmailForm({ new_email: '', current_password: '' });
        setTimeout(() => {
          setShowEmailForm(false);
          setEmailMessage({ type: '', text: '' });
        }, 2000);
      } else {
        setEmailMessage({ type: 'error', text: data.message || 'Failed to update email address' });
      }
    } catch (err) {
      setEmailMessage({ type: 'error', text: 'Connection error. Please try again.' });
    } finally {
      setEmailLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '20px'
    },
    header: {
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      padding: '16px 0',
      marginBottom: '20px',
      borderRadius: '8px'
    },
    headerContent: {
      width: '90%',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '24px',
      fontWeight: '500',
      color: '#333',
      margin: 0
    },
    backButton: {
      background: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    content: {
      width: '90%',
      maxWidth: '800px',
      margin: '0 auto'
    },
    card: {
      background: 'white',
      borderRadius: '8px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    },
    profileSection: {
      marginBottom: '30px'
    },
    profileHeader: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '20px',
      paddingBottom: '10px',
      borderBottom: '2px solid #e5e7eb'
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid #f3f4f6'
    },
    infoLabel: {
      fontWeight: '500',
      color: '#6b7280',
      fontSize: '14px'
    },
    infoValue: {
      color: '#333',
      fontSize: '14px'
    },
    actionButton: {
      background: '#4f46e5',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      marginTop: '20px'
    },
    formContainer: {
      marginTop: '20px'
    },
    formGroup: {
      marginBottom: '15px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontWeight: '500',
      fontSize: '14px',
      color: '#374151'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px'
    },
    submitButton: {
      background: '#4f46e5',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      opacity: loading ? 0.7 : 1
    },
    cancelButton: {
      background: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '20px',
      fontSize: '14px'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '20px',
      fontSize: '14px'
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>My Profile</h1>
          <button onClick={onBack} style={styles.backButton}>
            Back to Portal
          </button>
        </div>
      </header>

      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.profileSection}>
            <h2 style={styles.profileHeader}>Profile Information</h2>

            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Username</span>
              <span style={styles.infoValue}>{user.username}</span>
            </div>

            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Email</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={styles.infoValue}>{currentUser.email}</span>
                <button
                  onClick={() => setShowEmailForm(true)}
                  style={{
                    ...styles.actionButton,
                    padding: '4px 8px',
                    fontSize: '12px',
                    margin: 0
                  }}
                >
                  Change
                </button>
              </div>
            </div>

            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>First Name</span>
              <span style={styles.infoValue}>{user.first_name || 'Not set'}</span>
            </div>

            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Last Name</span>
              <span style={styles.infoValue}>{user.last_name || 'Not set'}</span>
            </div>
          </div>

          {/* Email Change Form */}
          {showEmailForm && (
            <div style={styles.profileSection}>
              <h2 style={styles.profileHeader}>Change Email Address</h2>

              {emailMessage.text && (
                <div style={emailMessage.type === 'success' ? styles.successMessage : styles.errorMessage}>
                  {emailMessage.text}
                </div>
              )}

              <div style={styles.formContainer}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>New Email Address</label>
                  <input
                    type="email"
                    style={styles.input}
                    value={emailForm.new_email}
                    onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                    placeholder="Enter new email address"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Current Password (for verification)</label>
                  <input
                    type="password"
                    style={styles.input}
                    value={emailForm.current_password}
                    onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleEmailChange();
                      }
                    }}
                    placeholder="Enter your current password"
                    required
                  />
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    onClick={handleEmailChange}
                    style={styles.submitButton}
                    disabled={emailLoading}
                  >
                    {emailLoading ? 'Updating...' : 'Update Email'}
                  </button>
                  <button
                    onClick={() => {
                      setShowEmailForm(false);
                      setEmailForm({ new_email: '', current_password: '' });
                      setEmailMessage({ type: '', text: '' });
                    }}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={styles.profileSection}>
            <h2 style={styles.profileHeader}>Security</h2>

            {message.text && (
              <div style={message.type === 'success' ? styles.successMessage : styles.errorMessage}>
                {message.text}
              </div>
            )}

            {!showPasswordForm ? (
              <button
                onClick={() => setShowPasswordForm(true)}
                style={styles.actionButton}
              >
                Change Password
              </button>
            ) : (
              <div style={styles.formContainer}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Current Password</label>
                  <input
                    type="password"
                    style={styles.input}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>New Password</label>
                  <input
                    type="password"
                    style={styles.input}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    required
                    minLength="8"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirm New Password</label>
                  <input
                    type="password"
                    style={styles.input}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handlePasswordChange();
                      }
                    }}
                    required
                    minLength="8"
                  />
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    onClick={handlePasswordChange}
                    style={styles.submitButton}
                    disabled={loading}
                  >
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                      setMessage({ type: '', text: '' });
                    }}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;